import { Button } from '@tablecheck/tablekit-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { ForgotPasswordLink } from 'components/ForgotPasswordLink';
import { PasskeyLoginLink } from 'components/PasskeyLoginLink';
import { SSOLinks } from 'pages/SignIn/components/SSOLinks';
import { SignInPageController } from 'pages/SignIn/machine';
import { authenticationBus } from 'useAuthorizationWatcher';
import { doRedirect } from 'utils/auth';
import { ROUTES } from 'utils/constants';
import { FormFooter } from 'utils/form.styles';

export function Footer(): JSX.Element {
  const [t, { language }] = useTranslation();
  const history = useHistory();
  const [state, send] = SignInPageController.useActor();
  const {
    context: { errorData }
  } = state;
  const isSubmitting = state.matches('unlocked.passwordLogin.submitting');

  if (
    state.matches('expiredPassword') &&
    (errorData?.error === 'password_expired' ||
      errorData?.error === 'password_uninitialized')
  ) {
    return (
      <Button
        data-size="large"
        onClick={() =>
          history.push(
            `/${language}/${ROUTES.RESET_PASSWORD}?token=${errorData.token}`
          )
        }
      >
        {t('forms.reset_password')}
      </Button>
    );
  }

  if (
    state.matches('expiringPassword') &&
    errorData?.error === 'password_expiring'
  ) {
    return (
      <FormFooter>
        <Button
          data-size="large"
          onClick={() => history.push(errorData.reset_uri)}
        >
          {t('forms.reset_password')}
        </Button>
        <Button
          data-size="large"
          data-variant="ghost"
          onClick={() => {
            authenticationBus.sendMessage();
            doRedirect(errorData.continue_uri);
          }}
        >
          {t('forms.sign_in')}
        </Button>
      </FormFooter>
    );
  }

  if (state.matches('usePasskeys') && errorData?.error === 'use_passkeys') {
    return (
      <FormFooter>
        {state.matches('usePasskeys.done') ? null : (
          <Button
            data-size="large"
            onClick={() => {
              send({ type: 'Create Passkey' });
            }}
          >
            {t(
              `forms.${
                state.matches('usePasskeys.failed')
                  ? 'register_passkey_again'
                  : 'register_passkey'
              }`
            )}
          </Button>
        )}
        <Button
          data-size="large"
          data-variant={state.matches('usePasskeys.done') ? 'primary' : 'ghost'}
          onClick={() => {
            authenticationBus.sendMessage();
            doRedirect(errorData.continue_uri);
          }}
        >
          {t(
            `forms.${
              state.matches('usePasskeys.done')
                ? 'register_passkey_continue'
                : 'skip_passkey_registration'
            }`
          )}
        </Button>
      </FormFooter>
    );
  }

  return (
    <>
      <Button
        id="submit-button"
        data-size="large"
        disabled={isSubmitting}
        aria-busy={isSubmitting}
        type="submit"
        aria-label="Submit button"
      >
        {t('forms.sign_in')}
      </Button>
      <FormFooter>
        <ForgotPasswordLink />
        <PasskeyLoginLink />
        <SSOLinks />
      </FormFooter>
    </>
  );
}
